import { getType } from 'typesafe-actions';

import {
  setActivePickTour,
  addPickTourPage,
  setActivePickTourPage,
  incrementActivePickPickedQuantity,
  setActivePickID,
  setHasJustScanned,
  setActivePick,
  skipLocation,
  setForwardingStore,
  updateQuantityOnHand,
  revertPickedQuantity,
  setCommercialSkipItemsAction,
  setEditingOrders,
} from '../actions/activePickTour.actions';

import { calculateAmountToPick, generatePickID, sortPicksByLocation, isRecallOrder } from '../../utils';
import { SKIP_ITEMS_ACTION_NONE } from '../../constants';

import { ActivePickTourState } from './types';
import { RootAction } from '../actions/types';

export default (state: ActivePickTourState = null, action: RootAction): ActivePickTourState => {
  switch (action.type) {
    case getType(setActivePickTour): {
      const sortedPicks = sortPicksByLocation({
        picks: action.payload.pickTour.picks,
        shouldUseFirstLocation: action.payload.pickTour.picks.some(pick => isRecallOrder(pick.orderType)),
      });

      // For RECALL orders, ALWAYS select the first item (no auto-skipping)
      // This allows users to review and adjust QOH even for 0/0 items
      // For other orders, find first pickable item
      const isRecall = isRecallOrder(sortedPicks[0]?.orderType);
      const selectedPick = isRecall
        ? sortedPicks[0]
        : sortedPicks.find(
            pick => pick.pickedQuantity < calculateAmountToPick(pick) && !pick.isSkipped && pick.isEligibleForPicking
          ) || sortedPicks[0];

      return {
        ...action.payload,
        pickTour: {
          ...action.payload.pickTour,
          picks: sortedPicks,
        },
        activePickID: generatePickID(selectedPick),
        forwardingStore: state ? state.forwardingStore : null,
        commercialSkipItemsAction: SKIP_ITEMS_ACTION_NONE,
        editingOrders: [],
      };
    }
    case getType(addPickTourPage): {
      if (state) {
        const sortedPicks = sortPicksByLocation({ picks: [...state.pickTour.picks, ...action.payload.picks] });

        // For RECALL orders, ALWAYS select the first item (no auto-skipping)
        // For other orders, find first pickable item
        const isRecall = isRecallOrder(sortedPicks[0]?.orderType);
        const selectedPick = isRecall
          ? sortedPicks[0]
          : sortedPicks.find(pick => pick.pickedQuantity < calculateAmountToPick(pick) && !pick.isSkipped) ||
            sortedPicks[0];

        return (
          state && {
            ...state,
            pickTour: {
              ...state.pickTour,
              picks: sortedPicks,
            },
            data: {
              ...state.data,
              pickTourList: [...state.data.pickTourList, ...action.payload.pickTourListData],
            },
            activePickID: generatePickID(selectedPick),
          }
        );
      }
      return state;
    }
    case getType(setActivePickTourPage): {
      const sortedPicks = sortPicksByLocation({ picks: action.payload.picks });

      // For RECALL orders, ALWAYS select the first item on the page (no auto-skipping)
      // This allows users to review and adjust QOH even for 0/0 items
      // For other orders, find first pickable item
      const isRecall = isRecallOrder(sortedPicks[0]?.orderType);
      const nextActivePick = isRecall
        ? sortedPicks[0]
        : sortedPicks.find(pick => pick.pickedQuantity < calculateAmountToPick(pick) && !pick.isSkipped) ||
          sortedPicks[0];

      return (
        state && {
          ...state,
          pickTour: {
            ...state.pickTour,
            picks: action.payload.picks,
          },
          data: {
            ...state.data,
            pickTourList: action.payload.pickTourListData,
          },
          activePickID: generatePickID(nextActivePick),
          hasJustScanned: false,
          isPaginated: true,
          paginationMetadata: action.payload.paginationMetadata,
        }
      );
    }
    case getType(incrementActivePickPickedQuantity): {
      return (
        state && {
          ...state,
          pickTour: {
            ...state.pickTour,
            picks: state.pickTour.picks.map(pick =>
              generatePickID(pick) === state.activePickID
                ? {
                    ...pick,
                    pickedQuantity: pick.pickedQuantity + action.payload.quantity,
                    locations: pick.locations.map((location, index) =>
                      index === pick.locationIndex
                        ? { ...location, pickedQuantity: location.pickedQuantity + action.payload.quantity }
                        : location
                    ),
                    isSkipped: false,
                    skipReason: null,
                  }
                : pick
            ),
          },
        }
      );
    }
    case getType(setActivePickID): {
      return (
        state && {
          ...state,
          activePickID: action.payload.id,
          pickTour:
            action.payload.locationIndex !== undefined
              ? {
                  ...state.pickTour,
                  picks: state.pickTour.picks.map(pick =>
                    generatePickID(pick) === action.payload.id
                      ? {
                          ...pick,
                          locationIndex: action.payload.locationIndex as number,
                        }
                      : pick
                  ),
                }
              : state.pickTour,
        }
      );
    }
    case getType(setHasJustScanned): {
      return (
        state && {
          ...state,
          hasJustScanned: action.payload.hasJustScanned,
        }
      );
    }
    case getType(setActivePick): {
      if (state) {
        const updatedPicks = state.pickTour.picks.map(pick =>
          generatePickID(pick) === action.payload.previousPickDetails.pickID
            ? {
                ...pick,
                ...action.payload.previousPickDetails,
                // Use the API response's pickedItemQuantity (total) instead of local pickedQuantity (delta)
                pickedQuantity: action.payload.data.pickedItemQuantity,
                // Reset all location pickedQuantities to 0 after successful API update
                // This ensures locations only track NEW picks from the current session
                locations: pick.locations.map(location => ({
                  ...location,
                  pickedQuantity: 0,
                })),
              }
            : pick
        );
        const activePickID = generatePickID(action.payload.activePick);
        const nonPaginationState = {
          ...state,
          pickTour: {
            ...state.pickTour,
            picks: updatedPicks,
          },
          data: {
            ...state.data,
            pickTourList: state.data.pickTourList.map(pickTourListItem =>
              `${pickTourListItem.orderType}:${pickTourListItem.orderID}:${pickTourListItem.pickTourItemDetailLineNumber}` ===
              `${action.payload.data.orderType}:${action.payload.data.orderID}:${action.payload.data.pickTourItemDetailLineNumber}`
                ? {
                    ...action.payload.data,
                    location: pickTourListItem.location,
                    itemDeliveryRoute: pickTourListItem.itemDeliveryRoute,
                    itemDispatchTime: pickTourListItem.itemDispatchTime,
                  }
                : pickTourListItem
            ),
          },
          activePickID,
          hasJustScanned: false,
        };
        if (state.isPaginated) {
          return {
            ...nonPaginationState,
            isPaginated: state.isPaginated,
            paginationMetadata: state.paginationMetadata,
          };
        }
        return { ...nonPaginationState, isPaginated: state.isPaginated };
      }
      return state;
    }
    case getType(updateQuantityOnHand): {
      return (
        state && {
          ...state,
          ...(state.isPaginated && {
            paginationMetadata: { ...state.paginationMetadata, totalItems: action.payload.totalItems },
          }),
          pickTour: {
            ...state.pickTour,
            picks: state.pickTour.picks.map(pick => {
              if (generatePickID(pick) === action.payload.activePickID) {
                const { updatedQuantity } = action.payload;
                const keepQty = pick.keepQuantityOnHand ?? 0;

                // For RECALL orders, calculate recallRequiresPicking based on QOH vs Keep
                const recallRequiresPicking = isRecallOrder(pick.orderType)
                  ? updatedQuantity !== null && updatedQuantity !== undefined && updatedQuantity >= keepQty
                  : pick.recallRequiresPicking;

                return {
                  ...pick,
                  pickedQuantity: action.payload.resetPickedQuantity ? 0 : pick.pickedQuantity,
                  locations: pick.locations.map((location, index) =>
                    index === pick.locations.length - 1
                      ? {
                          ...location,
                          pickedQuantity: action.payload.resetPickedQuantity ? 0 : location.pickedQuantity,
                          updatedQuantity: action.payload.updatedQuantity,
                        }
                      : {
                          ...location,
                          pickedQuantity: action.payload.resetPickedQuantity ? 0 : location.pickedQuantity,
                        }
                  ),
                  isSkipped: false,
                  skipReason: null,
                  recallRequiresPicking,
                };
              }
              return pick;
            }),
          },
        }
      );
    }
    case getType(skipLocation): {
      return (
        state && {
          ...state,
          pickTour: {
            ...state.pickTour,
            picks: action.payload.updatedPicks,
          },
          activePickID: generatePickID(action.payload.updatedNextPick),
          hasJustScanned: false,
        }
      );
    }
    case getType(setForwardingStore): {
      return (
        state && {
          ...state,
          forwardingStore: action.payload.storeID,
        }
      );
    }
    case getType(revertPickedQuantity): {
      return (
        state && {
          ...state,
          pickTour: {
            ...state.pickTour,
            picks: state.pickTour.picks.map(pick =>
              generatePickID(pick) === action.payload.pickID
                ? {
                    ...pick,
                    pickedQuantity: action.payload.previousPickedQuantity,
                    locations: pick.locations.map((location, index) => {
                      const previousLocationQty = action.payload.previousLocationQuantities.find(
                        loc => loc.locationIndex === index
                      );
                      return previousLocationQty
                        ? { ...location, pickedQuantity: previousLocationQty.pickedQuantity }
                        : location;
                    }),
                  }
                : pick
            ),
          },
        }
      );
    }
    case getType(setCommercialSkipItemsAction): {
      return (
        state && {
          ...state,
          commercialSkipItemsAction: action.payload.action,
        }
      );
    }
    case getType(setEditingOrders): {
      return (
        state && {
          ...state,
          editingOrders: action.payload.editingOrders,
        }
      );
    }
    default: {
      return state;
    }
  }
};
